import {
  ApiError,
  ApiErrorInitialState,
  GinApplication,
  Grant,
  GrantResult,
  MoveCardRequest,
  Program,
} from '@hellodarwin/core/lib/features/entities';
import { AcceptedProgramPreview } from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import {
  ClientProgramStatus,
  ProgramTargetUser,
  ProgramUpdateProperties,
} from '@hellodarwin/core/lib/features/entities/programs-entities';
import {
  EntityState,
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { RootState } from '../../../app/index';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApi from '../client-api';
import ClientApiV2 from '../client-api-v2';
import { subscribeNotifications } from './grants-notifications-slice';

const programsAdapter = createEntityAdapter({
  selectId: (model: Program) => model.program_id,
});
const grantsAdapter = createEntityAdapter({
  selectId: (model: Grant) => model.grant_id,
});

const acceptedProgramsAdapter = createEntityAdapter({
  selectId: (model: AcceptedProgramPreview) => model.program_id,
});

const ginApplicationAdapter = createEntityAdapter({
  selectId: (model: GinApplication) => model.application_id,
});

export const toggleProgramModal = createAction<{
  isVisible: boolean;
  type: string;
}>('client/toggleProgramModal');

export interface ProgramStates {
  status: 'idle' | 'pending';
  moveProgramCardStatus: 'idle' | 'pending';
  createProgramStatus: 'idle' | 'pending';
  acceptedProgramsStatus: 'idle' | 'pending';
  deleteProgramStatus: 'idle' | 'pending';
  error: ApiError;
  programs: EntityState<Program, string>;
  roadmapPrograms: EntityState<Program, string>;
  roadmapClientPrograms: EntityState<Program, string>;
  grants: EntityState<GrantResult, string>;
  programsByGrantId: { [grantId: string]: Program };
  acceptedPrograms: EntityState<AcceptedProgramPreview, string>;
  ginApplications: EntityState<GinApplication, string>;
  modal: {
    isVisible: boolean;
    type: string;
  };
}

const initialState: ProgramStates = {
  status: 'idle',
  moveProgramCardStatus: 'idle',
  createProgramStatus: 'idle',
  acceptedProgramsStatus: 'idle',
  deleteProgramStatus: 'idle',
  error: ApiErrorInitialState,
  programs: programsAdapter.getInitialState(),
  roadmapPrograms: programsAdapter.getInitialState(),
  roadmapClientPrograms: programsAdapter.getInitialState(),
  grants: grantsAdapter.getInitialState(),
  programsByGrantId: {},
  acceptedPrograms: acceptedProgramsAdapter.getInitialState(),
  ginApplications: ginApplicationAdapter.getInitialState(),
  modal: {
    isVisible: false,
    type: '',
  },
};

export const GetProgram = createAsyncThunk<
  Program,
  { api: ClientApi; programId: string; locale: string },
  { rejectValue: ApiError }
>(
  'client/GetProgram',
  async (
    {
      api,
      programId,
      locale,
    }: { api: ClientApi; programId: string; locale: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.getProgram(programId, locale);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const GetPrograms = createAsyncThunk<
  Program[],
  { api: ClientApi },
  { rejectValue: ApiError }
>(
  'client/GetPrograms',
  async ({ api }: { api: ClientApi }, { rejectWithValue }) => {
    try {
      return await api.getPrograms();
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const GetRoadmapPrograms = createAsyncThunk<
  Program[],
  { api: ClientApi; locale: string; excludeProgramGrantStatuses?: string[] },
  { rejectValue: ApiError }
>(
  'client/GetRoadmapPrograms',
  async (
    {
      api,
      locale,
      excludeProgramGrantStatuses,
    }: {
      api: ClientApi;
      locale: string;
      excludeProgramGrantStatuses?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.getRoadmapPrograms(
        locale,
        ProgramTargetUser.Admin,
        excludeProgramGrantStatuses,
      );
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const GetRoadmapClientPrograms = createAsyncThunk<
  Program[],
  { api: ClientApi; locale: string; excludeProgramGrantStatuses?: string[] },
  { rejectValue: ApiError }
>(
  'client/GetRoadmapClientPrograms',
  async (
    {
      api,
      locale,
      excludeProgramGrantStatuses,
    }: {
      api: ClientApi;
      locale: string;
      excludeProgramGrantStatuses?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.getRoadmapPrograms(
        locale,
        ProgramTargetUser.Client,
        excludeProgramGrantStatuses,
      );
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrant = createAsyncThunk<
  Grant,
  {
    api: ClientApi;
    grantId: string;
    locale: string;
    countGrantView?: boolean;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGrant',
  async (
    {
      api,
      grantId,
      locale,
      countGrantView,
    }: {
      api: ClientApi;
      grantId: string;
      locale: string;
      countGrantView?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrant(grantId, locale, undefined, countGrantView);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGrantForProgram = createAsyncThunk<
  GrantResult,
  {
    api: ClientApi;
    grantId: string;
    locale: string;
    programId: string;
    countGrantView?: boolean;
  },
  { rejectValue: ApiError }
>(
  'client/fetchGrantForProgram',
  async (
    {
      api,
      grantId,
      locale,
      programId,
      countGrantView,
    }: {
      api: ClientApi;
      grantId: string;
      locale: string;
      programId: string;
      countGrantView?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGrant(grantId, locale, programId, countGrantView);
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const moveProgramCard = createAsyncThunk<
  null,
  {
    api: ClientApiV2;
    moveCardRequest: MoveCardRequest;
    previousProgramId?: string;
  },
  { rejectValue: ApiError }
>(
  'client/moveProgramCard',
  async (
    {
      api,
      moveCardRequest,
    }: {
      api: ClientApiV2;
      moveCardRequest: MoveCardRequest;
      previousProgramId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.put<Program[]>(`/programs/move-display`, moveCardRequest);
      return null;
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateProgramProperties = createAsyncThunk<
  null,
  {
    api: ClientApiV2;
    programId: string;
    properties: ProgramUpdateProperties & {
      status?: string;
      contact_id?: string;
    };
  },
  { rejectValue: ApiError }
>(
  'client/updateProgramProperties',
  async (
    {
      api,
      programId,
      properties,
    }: {
      api: ClientApiV2;
      programId: string;
      properties: ProgramUpdateProperties & {
        status?: string;
        contact_id?: string;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      await api.put<Program[]>(`/programs/${programId}/properties`, properties);
      return null;
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const createProgram = createAsyncThunk<
  Program,
  {
    api: ClientApiV2;
    program: Program;
  },
  { rejectValue: ApiError }
>(
  'client/createProgram',
  async (
    {
      api,
      program,
    }: {
      api: ClientApiV2;
      program: Program;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<Program>(`/programs`, program);
      return response.data;
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const findProgramByGrantId = createAsyncThunk<
  Program | null,
  { api: ClientApiV2; grantId: string },
  { rejectValue: ApiError }
>(
  'client/findProgramByGrantId',
  async (
    {
      api,
      grantId,
    }: {
      api: ClientApiV2;
      grantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<Program | null>(
        `/programs/grant/${grantId}`,
      );
      return response.data;
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchAcceptedPrograms = createAsyncThunk<
  AcceptedProgramPreview[],
  {
    api: ClientApi;
    grantId: string;
    programIdToExclude: string;
    locale: string;
  },
  { rejectValue: ApiError }
>(
  'client/fetchAcceptedPrograms',
  async (
    {
      api,
      grantId,
      programIdToExclude,
      locale,
    }: {
      api: ClientApi;
      grantId: string;
      programIdToExclude: string;
      locale: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchAcceptedPrograms(
        grantId,
        programIdToExclude,
        locale,
      );
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGinApplication = createAsyncThunk<
  GinApplication,
  { api: ClientApi; applicationId: string },
  { rejectValue: ApiError }
>(
  'client/fetchGinApplication',
  async (
    { api, applicationId }: { api: ClientApi; applicationId: string },
    { rejectWithValue },
  ) => {
    try {
      return await api.fetchGinApplication(applicationId);
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteClientProgram = createAsyncThunk<
  null,
  { api: ClientApiV2; programId: string },
  { rejectValue: ApiError }
>(
  'client/deleteClientProgram',
  async (
    {
      api,
      programId,
    }: {
      api: ClientApiV2;
      programId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.delete(`/programs/${programId}`);
      return null;
    } catch (err: any) {
      showErrorNotification(err.response.data.error_code);
      return rejectWithValue(err.response.data);
    }
  },
);

const programsSlice = createSlice({
  name: 'programs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(GetProgram.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(GetProgram.fulfilled, (state, { payload }) => {
      state.programs = programsAdapter.setOne(state.programs, payload);
      state.status = 'idle';
    });
    builder.addCase(GetProgram.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
    builder.addCase(fetchGrant.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchGrant.fulfilled, (state, { payload }) => {
      state.grants = grantsAdapter.setOne(state.grants, payload);
      state.status = 'idle';
    });
    builder.addCase(fetchGrant.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
    builder.addCase(fetchGrantForProgram.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchGrantForProgram.fulfilled, (state, { payload }) => {
      state.grants = grantsAdapter.setOne(state.grants, payload);
      state.status = 'idle';
    });
    builder.addCase(fetchGrantForProgram.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
    builder.addCase(GetPrograms.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(GetPrograms.fulfilled, (state, { payload }) => {
      if (payload !== null) {
        state.programs = programsAdapter.setAll(state.programs, payload);
      }
      state.status = 'idle';
    });
    builder.addCase(GetPrograms.rejected, (state, { payload }) => {
      if (payload !== null) {
        state.error = payload ?? ApiErrorInitialState;
      }
      state.status = 'idle';
    });
    builder.addCase(GetRoadmapPrograms.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(GetRoadmapPrograms.fulfilled, (state, { payload }) => {
      if (payload !== null) {
        state.roadmapPrograms = programsAdapter.setAll(
          state.roadmapPrograms,
          payload,
        );
      }
      state.status = 'idle';
    });
    builder.addCase(GetRoadmapPrograms.rejected, (state, { payload }) => {
      if (payload !== null) {
        state.error = payload ?? ApiErrorInitialState;
      }
      state.status = 'idle';
    });

    builder.addCase(GetRoadmapClientPrograms.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(
      GetRoadmapClientPrograms.fulfilled,
      (state, { payload }) => {
        if (payload !== null) {
          state.roadmapClientPrograms = programsAdapter.setAll(
            state.roadmapClientPrograms,
            payload,
          );
        }
        state.status = 'idle';
      },
    );
    builder.addCase(GetRoadmapClientPrograms.rejected, (state, { payload }) => {
      if (payload !== null) {
        state.error = payload ?? ApiErrorInitialState;
      }
      state.status = 'idle';
    });

    builder.addCase(toggleProgramModal, (state, { payload }) => {
      state.modal = payload;
    });
    builder.addCase(subscribeNotifications.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(subscribeNotifications.fulfilled, (state, { payload }) => {
      if (!payload) return;
      for (const subscription of payload) {
        if (
          subscription.grant_id &&
          state.grants.entities[subscription.grant_id]
        ) {
          state.grants.entities[subscription.grant_id]!.grant_is_subscribed =
            true;
        }
      }
      state.status = 'idle';
    });
    builder.addCase(subscribeNotifications.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });

    builder.addCase(moveProgramCard.pending, (state, action) => {
      state.moveProgramCardStatus = 'pending';

      const { moveCardRequest, previousProgramId } = action.meta.arg;

      // 1. Update display_order for each program in the moves array
      if (!!moveCardRequest?.programs_display_orders) {
        for (const move of moveCardRequest.programs_display_orders) {
          const { program_id, display_order } = move;
          if (state.roadmapClientPrograms.entities[program_id]) {
            state.roadmapClientPrograms.entities[program_id].display_order =
              display_order;
          }
        }
      }

      // 2. Splice the moved program before previousProgramId (or at start)
      const movedId = moveCardRequest.program_id;

      // Remove from current position
      const currentIdx = state.roadmapClientPrograms.ids.indexOf(movedId);
      if (!state.roadmapClientPrograms.entities[movedId] || currentIdx === -1)
        return;

      state.roadmapClientPrograms.ids.splice(currentIdx, 1);

      // Find the new index to insert
      const prevIdx = previousProgramId
        ? state.roadmapClientPrograms.ids.indexOf(previousProgramId)
        : -1;
      const insertIdx = prevIdx + 1;

      state.roadmapClientPrograms.ids.splice(insertIdx, 0, movedId);

      // 3. Update modified_at and status
      if (!!moveCardRequest.status) {
        state.roadmapClientPrograms.entities[movedId].program_updated_at =
          new Date().toISOString();
        state.roadmapClientPrograms.entities[movedId].client_program_status =
          moveCardRequest.status as ClientProgramStatus;
      }

      // 4. Update program properties
      const programCopy = { ...state.roadmapClientPrograms.entities[movedId] };

      if (!!moveCardRequest.program_targeted_amount) {
        programCopy.program_targeted_amount =
          moveCardRequest.program_targeted_amount;
      }
      if (!!moveCardRequest.program_application_amount) {
        programCopy.program_application_amount =
          moveCardRequest.program_application_amount;
      }
      if (!!moveCardRequest.program_accepted_amount) {
        programCopy.program_accepted_amount =
          moveCardRequest.program_accepted_amount;
      }
      if (!!moveCardRequest.program_applied_at) {
        programCopy.program_applied_at = dayjs(
          moveCardRequest.program_applied_at,
        ).toDate();
      }
      if (!!moveCardRequest.program_result_at) {
        programCopy.program_result_at = dayjs(
          moveCardRequest.program_result_at,
        ).toDate();
      }
      if (!!moveCardRequest.program_refusal_reason) {
        programCopy.program_refusal_reason =
          moveCardRequest.program_refusal_reason;
      }

      state.roadmapClientPrograms = programsAdapter.setOne(
        state.roadmapClientPrograms,
        programCopy,
      );
    });
    builder.addCase(moveProgramCard.fulfilled, (state) => {
      state.moveProgramCardStatus = 'idle';
    });
    builder.addCase(moveProgramCard.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.moveProgramCardStatus = 'idle';
    });
    builder.addCase(updateProgramProperties.pending, (state, action) => {
      const { programId, properties } = action.meta.arg;

      const targetApp = Object.values(state.ginApplications.entities).find(
        (app) => app?.application_id === programId,
      );

      if (targetApp) {
        const updatedApp = { ...targetApp };

        if (properties.program_targeted_amount !== undefined) {
          updatedApp.application_targeted_amount =
            properties.program_targeted_amount;
        }
        if (properties.program_application_amount !== undefined) {
          updatedApp.application_submitted_amount =
            properties.program_application_amount;
        }
        if (properties.program_accepted_amount !== undefined) {
          updatedApp.application_accepted_amount =
            properties.program_accepted_amount;
        }
        if (properties.status !== undefined) {
          updatedApp.application_status = properties.status;
        }
        if (properties.program_prefix_title !== undefined) {
          updatedApp.program_prefix_title = properties.program_prefix_title;
        }
        state.ginApplications = ginApplicationAdapter.upsertOne(
          state.ginApplications,
          updatedApp,
        );
      }
    });

    builder.addCase(createProgram.pending, (state) => {
      state.createProgramStatus = 'pending';
    });
    builder.addCase(createProgram.fulfilled, (state, { payload }) => {
      state.programs = programsAdapter.setOne(state.programs, payload);
      // Keep roadmap client programs list in sync so the count updates without re-fetching
      state.roadmapClientPrograms = programsAdapter.setOne(
        state.roadmapClientPrograms,
        payload,
      );
      if (!!payload.program_grant_id) {
        state.programsByGrantId[payload.program_grant_id] = payload;
      }
      state.createProgramStatus = 'idle';
    });
    builder.addCase(createProgram.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.createProgramStatus = 'idle';
    });
    builder.addCase(findProgramByGrantId.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(findProgramByGrantId.fulfilled, (state, { payload }) => {
      if (!!payload) {
        state.programs = programsAdapter.setOne(state.programs, payload);
        if (!!payload.program_grant_id) {
          state.programsByGrantId[payload.program_grant_id] = payload;
        }
      }
      state.status = 'idle';
    });
    builder.addCase(findProgramByGrantId.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
    builder.addCase(fetchAcceptedPrograms.pending, (state) => {
      state.acceptedProgramsStatus = 'pending';
    });
    builder.addCase(fetchAcceptedPrograms.fulfilled, (state, { payload }) => {
      state.acceptedPrograms = acceptedProgramsAdapter.setAll(
        state.acceptedPrograms,
        payload ?? [],
      );
      state.acceptedProgramsStatus = 'idle';
    });
    builder.addCase(fetchAcceptedPrograms.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.acceptedProgramsStatus = 'idle';
    });
    builder.addCase(fetchGinApplication.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchGinApplication.fulfilled, (state, { payload }) => {
      state.ginApplications = ginApplicationAdapter.upsertOne(
        state.ginApplications,
        payload,
      );
      state.status = 'idle';
    });
    builder.addCase(fetchGinApplication.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });

    builder.addCase(deleteClientProgram.pending, (state, action) => {
      state.deleteProgramStatus = 'pending';
      const { programId } = action.meta.arg;
      if (state.roadmapClientPrograms.entities[programId]) {
        state.roadmapClientPrograms = programsAdapter.removeOne(
          state.roadmapClientPrograms,
          programId,
        );
      }
    });
    builder.addCase(deleteClientProgram.fulfilled, (state) => {
      state.deleteProgramStatus = 'idle';
    });
    builder.addCase(deleteClientProgram.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.deleteProgramStatus = 'idle';
    });
  },
});

export const selectProgramIsLoading = (state: RootState) =>
  state.program.status === 'pending';

export const selectProgramForTest = (state: RootState, grantId: string) =>
  state.program.programs.entities[grantId];

export const selectIsCreateProgramLoading = (state: RootState) =>
  state.program.createProgramStatus === 'pending';

export const selectIsAcceptedProgramsLoading = (state: RootState) =>
  state.program.acceptedProgramsStatus === 'pending';

export const { selectAll: selectAllPrograms, selectById: selectProgramById } =
  programsAdapter.getSelectors((state: RootState) => state.program.programs);
export const {
  selectAll: selectAllRoadmapPrograms,
  selectById: selectRoadmapProgramById,
} = programsAdapter.getSelectors(
  (state: RootState) => state.program.roadmapPrograms,
);
export const {
  selectAll: selectAllRoadmapClientPrograms,
  selectById: selectRoadmapClientProgramById,
} = programsAdapter.getSelectors(
  (state: RootState) => state.program.roadmapClientPrograms,
);

export const { selectAll: selectAllGrants, selectById: selectGrantById } =
  grantsAdapter.getSelectors((state: RootState) => state.program.grants);

export const selectProgramByGrantId = (state: RootState, grantId: string) =>
  state.program.programsByGrantId[grantId];

export const {
  selectAll: selectAllAcceptedPrograms,
  selectById: selectAcceptedProgramById,
} = acceptedProgramsAdapter.getSelectors(
  (state: RootState) => state.program.acceptedPrograms,
);

export const {
  selectAll: selectAllGinApplications,
  selectById: selectGinApplicationById,
} = ginApplicationAdapter.getSelectors(
  (state: RootState) => state.program.ginApplications,
);
export const selectProgramModal = (state: RootState) => state.program.modal;

export const programReducer = programsSlice.reducer;
